/**
 * TagItem - 단일 태그 아이템의 동작을 캡슐화
 *
 * 구조:
 *  [role=tag-item]
 *    ├─ input[js-tag-role=original] (저장된 태그만 해당)
 *    ├─ input[js-tag-role=new]
 *    └─ div[js-tag-role=editable][contenteditable]
 *
 * 유형 및 동작:
 *  SavedTagItem [js-tag-type=saved]:
 *    - 기존 태그 값 관리
 *    - 상태:
 *      - .deleted: 값이 비어있을 때
 *      - .changed: 값이 원본과 다를 때
 *      - (클래스 없음): 값이 원본과 같을 때
 *    - 액션:
 *      - delete: 삭제 상태로 표시 (DOM에는 유지)
 *      - restore: 원래 값으로 복원
 *
 *  NewTagItem [js-tag-type=new]:
 *    - 새로 추가된 태그 관리
 *    - 상태:
 *      - .changed: 값이 비어있지 않을 때
 *      - (클래스 없음): 값이 비어있을 때
 *    - 액션:
 *      - delete: DOM에서 제거
 *      - restore 기능 없음
 *
 * 공통 동작:
 *   - display div 클릭 시 editable div에 포커스
 *   - editable blur 시 input 값과 display 업데이트
 *   - 모든 값은 trim 처리됨
 *
 * 참고: 스타일링, 유효성 검사, 키보드 네비게이션은
 * TagItem 외부에서 처리됨
 */

// TagItem의 역할 : tag-item의 관점에서 필요한 기능을 encapsulate.

function assert(value) {
  if (!value) {
    throw new Error("value error")
  }
  return value
}

class BaseTagItem {
  constructor(element) {
    this.element = element
  }

  get newInput() {
    return assert(this.element.querySelector("input[js-tag-role=new]"))
  }

  get editableDiv() {
    return assert(this.element.querySelector("[contenteditable][js-tag-role=editable]"))
  }

  get value() {
    return this.newInput.value
  }

  get editableValue() {
    return this.editableDiv.textContent.trim()
  }

  get type() {
    return assert(this.element.getAttribute("js-tag-type"))
  }

  handleDisplayClick() {
    this.editableDiv.focus()
  }

  handleEditableBlur() {
    this.newInput.value = this.editableValue
  }

  handleEditableInput() {
    this.newInput.value = this.editableValue
  }
}

class SavedTagItem extends BaseTagItem {
  constructor(element) {
    super(element)
  }

  get originalInput() {
    return assert(this.element.querySelector("input[js-tag-role=original]"))
  }

  get oldValue() {
    return this.originalInput.value
  }

  get isDeleted() {
    return this.value === ""
  }

  get isChanged() {
    return this.value !== this.oldValue
  }

  handleEditableBlur() {
    super.handleEditableBlur()
    if (this.isDeleted) {
      // 정책상 삭제는 임시로 막아둠
      this.restore()
    } else {
      this.#syncView()
    }
  }

  delete() {
    this.newInput.value = ""
    this.editableDiv.textContent = this.oldValue
    this.#syncView()
  }

  restore() {
    this.newInput.value = this.oldValue
    this.editableDiv.textContent = this.oldValue
    this.#syncView()
  }

  #syncView() {
    if (this.isDeleted) {
      this.element.classList.add("deleted")
      this.element.classList.remove("changed")
      return
    }
    if (this.isChanged) {
      this.element.classList.add("changed")
      this.element.classList.remove("deleted")
      return
    }
    this.element.classList.remove("changed")
    this.element.classList.remove("deleted")
  }
}

class NewTagItem extends BaseTagItem {
  constructor(element) {
    super(element)
    this.#syncView()
  }

  get isChanged() {
    return this.value !== ""
  }

  handleEditableBlur() {
    super.handleEditableBlur()
    this.#syncView()
  }

  delete() {
    this.element.remove()
  }

  #syncView() {
    if (this.isChanged) {
      this.element.classList.add("changed")
    } else {
      this.element.classList.remove("changed")
    }
  }
}

class TagItemBuilder {
  static build(element) {
    const type = element.getAttribute("js-tag-type")
    switch (type) {
      case "saved":
        return new SavedTagItem(element)
      case "new":
        return new NewTagItem(element)
      default:
        throw new Error("invalid tag item type")
    }
  }
}

export default TagItemBuilder
